import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Location, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import translationKeys from '@translationKeys'

import BreadCrumbButtonList, { BreadCrumbButtonItem, BreadCrumbButtonListItemProps } from '@components/BreadCrumbButtonList'
import ScrollToTop from '@components/ScrollToTop'
import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import chatApi from '@modules/chat/api'
import searchApi from '@modules/search/api'
import { Box } from '@mui/material'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useQuery } from '@tanstack/react-query'

import {
  CompanyAssetHistoryProfile,
  CompanyHistoryProfile,
  DelegateHistoryProfile,
  ProfileType,
  isCompany,
  isCompanyAsset,
} from '../search/types'
import AssetProfile from './AssetProfile'
import AssetsOverview from './AssetsOverview/index'
import CompanyProfile from './CompanyProfile'
import DelegateProfile from './DelegateProfile'
import DelegatesOverview from './DelegatesOverview'
import useFetchProfile from './hooks/useFetchProfile'
import useNumberOfLatestAssets from './hooks/useNumberOfLatestAssets'
import {
  NavigationContainer,
  ProfileViewportWrapper,
  StyledKeyboardArrowLeftIcon,
  StyledProfileWrapper,
  StyledProfileWrapperInner,
  UpdatedCountBadge,
} from './index.styles'
import { getDefaultEventByCompanyData } from './utils'

const profileTypesByPath: Record<string, ProfileType> = {
  company: ProfileType.company,
  delegates: ProfileType.company,
  delegate: ProfileType.delegate,
  medtech: ProfileType.medtech,
  biotech: ProfileType.biotech,
  services: ProfileType.services,
  technologies: ProfileType.technologies,
}

const Profile = () => {
  const scrollRef = useRef(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location: Location<{ navigateByCrumbdsClick?: boolean }> = useLocation()

  const { profileId } = useParams<{ profileId: string }>()

  const { hasAccessToAlerts, hasUnlimitiedAccessToProfiles } = useFeatureFlags()

  const handleGoBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const profileType = useMemo(() => {
    const foundKey = Object.keys(profileTypesByPath).find(key => location.pathname.includes(key))

    return foundKey ? profileTypesByPath[foundKey] : ProfileType.company
  }, [location.pathname])

  const {
    data: isAccessibleResponse,
    isLoading: isAvailabilityLoading,
    isFetching: isAvailabilityFetching,
  } = useQuery({
    queryKey: [chatApi.checkProfileAccess.fn, profileId],
    enabled: !!profileId && !!profileType && !hasUnlimitiedAccessToProfiles,
    queryFn: () => chatApi.checkProfileAccess.fn({ profileId, profileType }),
  })

  const isUnavailableProfile =
    !location.state?.navigateByCrumbdsClick &&
    !isAvailabilityLoading &&
    !isAvailabilityFetching &&
    (!isAccessibleResponse || !isAccessibleResponse.isAccessible) &&
    !hasUnlimitiedAccessToProfiles

  useEffect(() => {
    if (isUnavailableProfile) {
      navigate('/plus/hub')
    }
  }, [isUnavailableProfile, navigate])

  const {
    data: profileData,
    status,
    isError,
  } = useFetchProfile({
    type: profileType,
    id: profileId,
    disabled: isAvailabilityLoading || isUnavailableProfile,
    onError: () => {
      navigate('/plus/hub', { replace: true })
    },
  })

  const { profilesAlertsMap } = useAlertDataForProfiles(
    profileData ? [{ id: profileData.id!, '@type': profileData['@type'] }] : [],
    hasAccessToAlerts && !!profileData,
  )
  const profileAlert = profileData && profilesAlertsMap[profileData.id]

  const {
    data: newAssetsCount,
    isLoading: isNewAssetsCountLoading,
    isFetching: isNewAssetsCountFetching,
  } = useQuery<{ count: number }>({
    queryKey: [searchApi.getNewAssetsCount.key, profileData?.id, profileAlert?.lastViewedAt],
    queryFn: () => searchApi.getNewAssetsCount.fn({ companyId: profileData?.id, lastViewedAt: profileAlert?.lastViewedAt }),
    enabled: !!profileData && !!profileAlert?.lastViewedAt && isCompany(profileData),
    refetchOnMount: 'always',
  })

  const isPrivateProfile = profileData?.latest?.isPrivate

  const companyId = useMemo(() => {
    if (!profileData) {
      return null
    }

    if (isCompany(profileData)) {
      return profileData.id
    }

    return profileData.latest.companyId
  }, [profileData])

  const { data: companyDelegates } = useQuery({
    queryKey: [searchApi.getCompanyDelegates.key, companyId],
    queryFn: () => searchApi.getCompanyDelegates.fn({ companyId: companyId!, size: '0' }),
    enabled: !!companyId,
  })

  const companyNewAssetsCount = useMemo(() => {
    if (isNewAssetsCountLoading || isNewAssetsCountFetching) {
      return 0
    }

    return newAssetsCount?.count ?? 0
  }, [newAssetsCount, isNewAssetsCountLoading, isNewAssetsCountFetching])

  const { latestAssetsTotal } = useNumberOfLatestAssets(companyId)
  const { data: companyData } = useFetchProfile({ type: ProfileType.company, id: companyId })

  const goToAllProductsPage = useCallback(
    (eventId?: number) => {
      if (!companyData?.id) {
        return
      }

      navigate(`/plus/profile/${companyData?.id}/products?event_id=${eventId || ''}`, { state: { navigateByCrumbdsClick: true } })
    },
    [companyData?.id, navigate],
  )
  const hasAdditionalAssets = useMemo(() => {
    if (!profileData || !companyData) return false

    if (isCompanyAsset(profileData)) return latestAssetsTotal && latestAssetsTotal > 1

    return companyData.latest.hasAssets
  }, [companyData, profileData, latestAssetsTotal])

  const crumbs = useMemo(() => {
    const list = [
      {
        id: 'profile_navigation_go_back',
        icon: <StyledKeyboardArrowLeftIcon />,
        label: t(translationKeys.COMMON_GO_BACK),
        onClick: handleGoBack,
      },
    ] as BreadCrumbButtonListItemProps[]

    if (!profileData || !companyData) {
      return list
    }

    list.push({
      id: 'profile_navigation-company_profile_tab',
      label: t(translationKeys.PROFILES_BREADCRUMBDS_COMPANY_PROFILE),
      onClick: () =>
        navigate(`/plus/profile/${companyData?.id}/${ProfileType.company}`, { state: { navigateByCrumbdsClick: true } }),
      isActive: location.pathname.includes('company'),
    })

    if (hasAdditionalAssets && companyData && !isPrivateProfile) {
      list.push({
        id: 'profile_navigation-all_products_tab',
        label: t(translationKeys.PROFILES_BREADCRUMBDS_ALL_PRODUCTS),
        render: () => (
          <Box position="relative">
            {!!companyNewAssetsCount && (
              <UpdatedCountBadge>{Number(companyNewAssetsCount) > 99 ? '99+' : companyNewAssetsCount}</UpdatedCountBadge>
            )}
            <BreadCrumbButtonItem
              key={t(translationKeys.PROFILES_BREADCRUMBDS_ALL_PRODUCTS)}
              isActive={location.pathname.includes('products')}
              onClick={() => goToAllProductsPage(profileData.latest?.eventData?.id)}
              label={t(translationKeys.PROFILES_BREADCRUMBDS_ALL_PRODUCTS)}
            />
          </Box>
        ),
      })
    }

    if (companyDelegates?.total && !isPrivateProfile) {
      list.push({
        id: 'profile_navigation-all_delegates_tab',
        label: t(translationKeys.PROFILES_BREADCRUMBDS_ALL_DELEGATES),
        onClick: () => navigate(`/plus/profile/${companyData?.id}/delegates`, { state: { navigateByCrumbdsClick: true } }),
        isActive: location.pathname.includes('delegates'),
      })
    }

    return list
  }, [
    location.pathname,
    profileData,
    companyData,
    goToAllProductsPage,
    hasAdditionalAssets,
    companyNewAssetsCount,
    companyDelegates,
    isPrivateProfile,
    navigate,
    t,
    handleGoBack,
  ])

  const isFetching = status === 'pending'

  const defaultEvenIdtAssetsOverview = useMemo(
    () => (companyData ? getDefaultEventByCompanyData(companyData) : undefined),
    [companyData],
  )

  return (
    <StyledProfileWrapper>
      <ScrollToTop scrollRef={scrollRef} />
      <StyledProfileWrapperInner>
        <NavigationContainer>
          <BreadCrumbButtonList list={crumbs} />
        </NavigationContainer>
        <ProfileViewportWrapper ref={scrollRef}>
          <Routes>
            <Route
              path="/company"
              element={
                <CompanyProfile
                  isAllDelegatesPageEnabled={Boolean(companyDelegates?.total && !isPrivateProfile)}
                  isError={isError}
                  isFetching={isFetching}
                  profile={profileData as CompanyHistoryProfile}
                  profileAlert={profileAlert}
                />
              }
            />
            <Route path="/delegates" element={<DelegatesOverview companyId={companyData?.id} />} />
            <Route
              path="/products"
              element={<AssetsOverview companyId={companyData?.id} eventId={defaultEvenIdtAssetsOverview} />}
            />
            <Route
              path="/medtech"
              element={
                <AssetProfile
                  isError={isError}
                  isFetching={isFetching}
                  profile={profileData as CompanyAssetHistoryProfile}
                  profileAlert={profileAlert}
                />
              }
            />
            <Route
              path="/services"
              element={
                <AssetProfile
                  isError={isError}
                  isFetching={isFetching}
                  profile={profileData as CompanyAssetHistoryProfile}
                  profileAlert={profileAlert}
                />
              }
            />
            <Route
              path="/biotech"
              element={
                <AssetProfile
                  isError={isError}
                  isFetching={isFetching}
                  profile={profileData as CompanyAssetHistoryProfile}
                  profileAlert={profileAlert}
                />
              }
            />
            <Route
              path="/technologies"
              element={
                <AssetProfile
                  isError={isError}
                  isFetching={isFetching}
                  profile={profileData as CompanyAssetHistoryProfile}
                  profileAlert={profileAlert}
                />
              }
            />
            <Route
              path="/delegate"
              element={
                <DelegateProfile
                  profile={profileData as DelegateHistoryProfile}
                  isError={isError}
                  isFetching={isFetching}
                  profileAlert={profileAlert}
                />
              }
            />
            <Route path="*" element={<Navigate to="/plus/hub" replace />} />
          </Routes>
        </ProfileViewportWrapper>
      </StyledProfileWrapperInner>
    </StyledProfileWrapper>
  )
}

export default Profile
